/**
 * Enhanced error handling utilities for LLM integration.
 *
 * Provides specific error messages and fallback strategies following
 * existing RECORD error handling patterns.
 */

/**
 * Get user-friendly error message based on error type.
 */
export const getErrorMessage = (error: any): string => {
    const errorMessage = error?.message || error?.toString() || '';

    if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
        return 'Summary generation is taking longer than expected. Please try the detailed view.';
    }

    if (errorMessage.includes('rate limit') || errorMessage.includes('throttling')) {
        return 'Summary service is busy. Please try again in a few moments.';
    }

    if (errorMessage.includes('model unavailable') || errorMessage.includes('service unavailable')) {
        return 'Summary service is temporarily unavailable. Please use the detailed comparison.';
    }

    if (errorMessage.includes('invalid') || errorMessage.includes('malformed')) {
        return 'Unable to process the contract comparison for summary. Please try the detailed view.';
    }

    // Generic fallback message
    return 'Unable to generate summary. Please try the detailed comparison instead.';
};

/**
 * Determine if an error should trigger automatic fallback to table view.
 */
export const shouldAutoFallback = (error: any): boolean => {
    const errorMessage = error?.message || error?.toString() || '';

    // Auto-fallback for critical errors that won't resolve with retry
    return (
        errorMessage.includes('model unavailable') ||
        errorMessage.includes('service unavailable') ||
        errorMessage.includes('invalid') ||
        errorMessage.includes('malformed')
    );
};

/**
 * Determine if an error supports retry functionality.
 */
export const isRetryableError = (error: any): boolean => {
    const errorMessage = error?.message || error?.toString() || '';

    // Retryable errors are typically transient
    return (
        errorMessage.includes('timeout') ||
        errorMessage.includes('rate limit') ||
        errorMessage.includes('throttling') ||
        errorMessage.includes('network')
    );
};

/**
 * Get retry delay in milliseconds based on error type.
 */
export const getRetryDelay = (error: any, attemptNumber: number): number => {
    const errorMessage = error?.message || error?.toString() || '';

    if (errorMessage.includes('rate limit') || errorMessage.includes('throttling')) {
        // Exponential backoff for rate limiting
        return Math.min(1000 * Math.pow(2, attemptNumber), 10000);
    }

    if (errorMessage.includes('timeout')) {
        // Fixed delay for timeouts
        return 2000;
    }

    // Default retry delay
    return 1000;
};
